import { Dispatch } from '@reduxjs/toolkit';
import { getDeerReviewFormRoute, getDeerReviewResultRoute } from 'Deer/constants/nonDeerRoutes';
import { addHours, isPast } from 'date-fns';
import { RouteComponentProps } from 'react-router-dom';
import { checkIntentLandingPagesFeatureEnabled } from 'utils/helpers/featureFlags';

import {
  AddPerson,
  BulletList,
  CheckOutlineCircle,
  CloseOutlineCircle,
  Download,
} from 'assets/assetsV1/iconComponents';

import {
  REVIEW_FORM_COMPLETION_STATUS,
  REVIEW_PROVIDER_TYPES,
  REVIEW_SUBMISSION_STATUS_MAP,
  TOAST_STATUSES,
} from 'constants/ProjectConstants';
import { downloadOrOpenFile, getReviewFormRoute, getReviewResultRoute } from 'constants/helper';
import { localStorageKeys } from 'constants/localStorageKeys';
import {
  sendOpenedNominationPanelEvent,
  sendReviewFormPageViewEvent,
  sendReviewSummaryPageViewEvent,
} from 'constants/mixpanelHelper';
import {
  sendClickedCompletedByMeViewDetailsEvent,
  sendClickedOnAcknowledgeEvent,
  sendClickedOnContinueReviewEvent,
  sendClickedOnEditNominationsEvent,
  sendClickedOnNominateEvent,
  sendClickedOnStartReviewEvent,
  sendClickedOnWithdraw,
  sendClickedPastReviewsViewDetailsEvent,
  sendRespondedToReviewRequestEvent,
} from 'constants/mixpanelHelpers/reviewsEvents';

import { reviewNotify } from 'services/analytics/review';
import { showNominatePeers } from 'services/nominatePeers';
import { acceptNomination, rejectNomination } from 'services/performanceReview';

import { AgGridKebabMenuOptions } from 'uiToolkitV1/AgGridWrapperV2/types';

export const isNominated = (review_) =>
  ['PEER', 'DLM'].includes(review_?.providerType) && review_?.peerStatus === 'NOMINATED';

export const getIsPendingForAcknowledgement = ({ reviewStatus }) =>
  reviewStatus === REVIEW_FORM_COMPLETION_STATUS.PUBLISHED;

export const generatePendingOnMeOptions = ({
  data,
  updatePeerStatus,
  resetData,
  history,
  dispatch,
  loggedInEmployee,
  toast,
  isNewNavEnabled,
}: {
  data: {
    [key: string]: any;
  };
  updatePeerStatus: (reviewId, employeeId, providerType) => {};
  resetData: () => {};
  history: RouteComponentProps['history'];
  dispatch: Dispatch<any>;
  loggedInEmployee: any;
  toast: any;
  isNewNavEnabled: boolean;
}) => {
  const { review, nominationsCount } = Object.values(data)[0]?.reviewItem ?? {};
  const reviewStatus = data[6]?.value;
  const ActionIcon = REVIEW_SUBMISSION_STATUS_MAP?.[review.submissionStatus]?.Icon;

  const isUserNominated = isNominated(review);
  const isPendingForAcknowledgement = getIsPendingForAcknowledgement({ reviewStatus });

  const goToFormReviewRoute = (reviewId, employeeId) => {
    const rabbitRoute = getReviewFormRoute(reviewId, employeeId);
    const deerRoute = getDeerReviewFormRoute(reviewId, employeeId);
    const routeToGo = isNewNavEnabled ? deerRoute : rabbitRoute;
    history.push(routeToGo);
  };

  const handleViewDetailsClick = () => {
    goToFormReviewRoute(review.feedbackForm.id, review.receiverEmployeeId);
    sendReviewFormPageViewEvent({ eventProperties: { source: 'Reviews pending on me' } });
  };

  const handleNominateClick = () => {
    sendOpenedNominationPanelEvent({
      eventProperties: {
        reviewCycleId: review.id,
        source: 'MyReviewPendingOnMe',
        empId: loggedInEmployee.id,
      },
    });
    if (nominationsCount === 0) {
      sendClickedOnNominateEvent({
        eventProperties: {
          reviewCycleId: review.id,
          source: 'MyReviewPendingOnMe',
          empId: loggedInEmployee.id,
        },
      });
    } else {
      sendClickedOnEditNominationsEvent({
        eventProperties: {
          reviewCycleId: review.id,
          empId: review.receiverEmployeeId,
          source: 'MyReviewsPendingOnMe',
        },
      });
    }
    // @ts-ignore
    dispatch(showNominatePeers({ reviewId: review.feedbackForm.id, onSubmit: resetData }));
  };

  const handleAcknowledgeClick = () => {
    sendClickedOnAcknowledgeEvent({
      eventProperties: {
        reviewCycleId: review.feedbackForm.id,
        empId: review.receiverEmployeeId,
        source: 'MyReviewsPendingOnMe',
      },
    });
    const routeToGo = isNewNavEnabled
      ? `${getDeerReviewResultRoute(
          review.feedbackForm.id,
          review.receiverEmployeeId
        )}?autoNavigate=true&source=Me&subSource=PENDING_ON_ME`
      : `${getReviewResultRoute(review.feedbackForm.id, review.receiverEmployeeId)}?autoNavigate=true`;
    history.push(routeToGo);
  };

  const handleSubmissionStatusClick = () => {
    if (review.summaryFilePath) {
      downloadOrOpenFile(review.summaryFilePath);
    } else {
      if (review.submissionStatus === REVIEW_FORM_COMPLETION_STATUS.NOT_STARTED) {
        sendClickedOnStartReviewEvent({
          eventProperties: {
            reviewCycleId: review.feedbackForm.id,
            empId: review.receiverEmployeeId,
            source: 'MyReviewsPendingOnMe',
          },
        });
      } else if (review.submissionStatus === REVIEW_FORM_COMPLETION_STATUS.IN_PROGRESS) {
        sendClickedOnContinueReviewEvent({
          eventProperties: {
            reviewCycleId: review.feedbackForm.id,
            empId: review.receiverEmployeeId,
            source: 'MyReviewsPendingOnMe',
          },
        });
      }
      handleViewDetailsClick();
    }
  };

  const handleAcceptClick = () => {
    sendRespondedToReviewRequestEvent({
      eventProperties: {
        reviewCycleId: review.feedbackForm.id,
        empId: review.receiverEmployeeId,
        source: 'MyReviewsPendingOnMe',
        value: 'Accepted',
      },
    });
    acceptNomination({
      reviewId: review.feedbackForm.id,
      employeeId: review.receiverEmployeeId,
      isDLM: review.providerType === REVIEW_PROVIDER_TYPES.DLM,
    })
      .then(() => {
        updatePeerStatus(review.feedbackForm.id, review.receiverEmployeeId, review.providerType);
      })
      .catch((err) => {
        toast({
          title: err.message || 'Some error occurred',
          status: TOAST_STATUSES.ERROR,
          position: 'bottom-left',
        });
      });
  };

  const handleRejectClick = () => {
    sendRespondedToReviewRequestEvent({
      eventProperties: {
        reviewCycleId: review.feedbackForm.id,
        empId: review.receiverEmployeeId,
        source: 'MyReviewsPendingOnMe',
        value: 'Rejected',
      },
    });
    rejectNomination({
      reviewId: review.feedbackForm.id,
      employeeId: review.receiverEmployeeId,
      isDLM: review.providerType === REVIEW_PROVIDER_TYPES.DLM,
    })
      .then(() => {
        // Resetting data of table is the only option as pagination breaks when we reject any nomination.
        resetData();
      })
      .catch((err) => {
        toast({
          title: err.message || 'Some error occurred',
          status: TOAST_STATUSES.ERROR,
          position: 'bottom-left',
        });
      });
  };

  const options = [] as AgGridKebabMenuOptions;

  if (isPendingForAcknowledgement && !review.summaryFilePath) {
    options.push({
      label: 'Acknowledge',
      onClick: handleAcknowledgeClick,
      Icon: CheckOutlineCircle,
    });
  }

  if (!isPendingForAcknowledgement && review.providerType === 'NOMINATOR') {
    options.push({
      label: nominationsCount === 0 ? 'Nominate' : 'Edit Nominations',
      onClick: handleNominateClick,
      Icon: AddPerson,
    });
  }

  if (!isPendingForAcknowledgement && review.providerType !== 'NOMINATOR') {
    if (isUserNominated) {
      options.push({
        label: 'Accept',
        onClick: handleAcceptClick,
        Icon: CheckOutlineCircle,
      });
      options.push({
        label: 'Deny',
        onClick: handleRejectClick,
        Icon: CloseOutlineCircle,
      });
    } else {
      options.push({
        label: REVIEW_SUBMISSION_STATUS_MAP[review.submissionStatus].actionText,
        onClick: handleSubmissionStatusClick,
        Icon: ActionIcon,
      });
    }
  }

  return options;
};

export const generatePendingOnOthersOptions = ({
  data,
  forceUpdate,
  toast,
  setEmployeeBeingWithdrawn,
  setReviewBeingWithdrawn,
  onOpen,
}: {
  data: {
    [key: string]: any;
  };
  forceUpdate: () => {};
  toast: any;
  setEmployeeBeingWithdrawn: (employee) => {};
  setReviewBeingWithdrawn: (review) => {};
  onOpen: () => {};
}) => {
  const { review, employee, allowReviewFormWithdraw } = Object.values(data)[0]?.reviewItem ?? {};

  const openWithdrawModal = () => {
    sendClickedOnWithdraw({
      eventProperties: {
        reviewCycleId: review.feedbackForm.id,
        empId: review.receiverEmployeeId,
        source: 'MyReviewsPendingOnOthers',
      },
    });
    setEmployeeBeingWithdrawn(employee);
    setReviewBeingWithdrawn(review);
    onOpen();
  };

  const handleNotify = async () => {
    try {
      const response = await reviewNotify({
        feedbackFormId: review.feedbackForm.id,
        receiverEmployeeId: review.receiverEmployeeId,
        providerEmployeeId: review.providerEmployeeId,
        providerType: review.providerType,
      });
      localStorage.setItem(
        `${localStorageKeys.lastNotifiedReviewTime}-${review.feedbackForm.id}-${review.providerEmployeeId}`,
        // @ts-ignore
        addHours(new Date(), 24)
      );
      toast({
        title: response.message,
        status: TOAST_STATUSES.SUCCESS,
        position: 'bottom-left',
      });
      forceUpdate();
    } catch (error) {
      console.error('Error in notifying', error);
      toast({
        title: error.message || 'Some error occurred',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
    }
  };

  const isNotifyDisabled = isPast(
    new Date(
      localStorage.getItem(
        `${localStorageKeys.lastNotifiedReviewTime}-${review.feedbackForm.id}-${review.providerEmployeeId}`
      )
    )
  );

  const options = [] as AgGridKebabMenuOptions;

  options.push({
    label: !isNotifyDisabled ? 'Notified' : 'Notify',
    onClick: handleNotify,
    Icon: AddPerson,
    disabled: !isNotifyDisabled,
    disabledReason: 'You have notified, please wait 24 hours before you notify again.',
  });

  if (allowReviewFormWithdraw) {
    options.push({
      label: 'Withdraw',
      onClick: openWithdrawModal,
      Icon: CloseOutlineCircle,
    });
  }

  return options;
};

export const generateCompletedByMeOptions = ({
  history,
  data,
  loggedInEmployee,
  selectedTableId,
  isNewNavEnabled,
}: {
  history: RouteComponentProps['history'];
  data: {
    [key: string]: any;
  };
  loggedInEmployee: any;
  selectedTableId: string;
  isNewNavEnabled: boolean;
}) => {
  const goToResultReviewRoute = (reviewId, employeeId) => {
    const routeToGo = isNewNavEnabled
      ? `${getDeerReviewResultRoute(reviewId, employeeId)}?source=${
          checkIntentLandingPagesFeatureEnabled() ? 'Me' : selectedTableId
        }&subSource=${selectedTableId}&defaultSelectedProviderId=${loggedInEmployee.id}`
      : `${getReviewResultRoute(reviewId, employeeId)}?source=${selectedTableId}&defaultSelectedProviderId=${
          loggedInEmployee.id
        }`;
    history.push(routeToGo);
  };
  const handleViewDetailsClick = () => {
    const { review, employee } = Object.values(data)[0]?.reviewItem ?? {};
    sendReviewSummaryPageViewEvent({ eventProperties: { source: 'My past reviews' } });

    sendClickedCompletedByMeViewDetailsEvent({
      eventProperties: {
        reviewCycleId: review.id,
        empId: employee.id,
        source: 'MyReviewsCompleted',
      },
    });
    goToResultReviewRoute(review.feedbackForm.id, review.receiverEmployeeId);
  };
  const options = [] as AgGridKebabMenuOptions;

  options.push({
    label: 'View Details',
    onClick: handleViewDetailsClick,
    Icon: BulletList,
  });

  return options;
};

export const generatePastReviewsOptions = ({
  history,
  data,
  loggedInEmployee,
  selectedTableId,
  isNewNavEnabled,
}: {
  history: RouteComponentProps['history'];
  data: {
    [key: string]: any;
  };
  loggedInEmployee: any;
  selectedTableId: string;
  isNewNavEnabled: boolean;
}) => {
  const { review, isFormAcknowledged } = Object.values(data)[0]?.reviewItem ?? {};
  const isPendingForAcknowledgement = !isFormAcknowledged;

  const goToFormReviewRoute = (reviewId, employeeId) => {
    const routeToGo = isNewNavEnabled
      ? `${getDeerReviewResultRoute(reviewId, employeeId)}?source=${
          checkIntentLandingPagesFeatureEnabled() ? 'Me' : selectedTableId
        }&subSource=${selectedTableId}&defaultSelectedProviderId=${loggedInEmployee.id}`
      : `${getReviewResultRoute(reviewId, employeeId)}?source=${selectedTableId}&defaultSelectedProviderId=${
          loggedInEmployee.id
        }`;
    history.push(routeToGo);
  };

  const handleAcknowledgeClick = () => {
    sendClickedOnAcknowledgeEvent({
      eventProperties: {
        reviewCycleId: review.feedbackForm.id,
        empId: review.receiverEmployeeId,
        source: 'MyReviewsPastReviews',
      },
    });
    const routeToGo = isNewNavEnabled
      ? `${getDeerReviewResultRoute(
          review.feedbackForm.id,
          review.receiverEmployeeId
        )}?autoNavigate=true&source=Me&subSource=${selectedTableId}`
      : `${getReviewResultRoute(review.feedbackForm.id, review.receiverEmployeeId)}?autoNavigate=true`;
    history.push(routeToGo);
  };

  const handleViewDetailsClick = () => {
    // eslint-disable-next-line no-shadow
    const { review } = Object.values(data)[0]?.reviewItem ?? {};
    sendReviewSummaryPageViewEvent({ eventProperties: { source: 'My past reviews' } });

    sendClickedPastReviewsViewDetailsEvent({
      eventProperties: {
        reviewCycleId: review.id,
        empId: loggedInEmployee.id,
        source: 'MyReviewsPast',
      },
    });
    goToFormReviewRoute(review.feedbackForm.id, review.receiverEmployeeId);
  };

  const handleDownloadFile = () => {
    downloadOrOpenFile(review.summaryFilePath);
  };

  const options = [] as AgGridKebabMenuOptions;

  if (isPendingForAcknowledgement && !review.summaryFilePath) {
    options.push({
      label: 'Acknowledge',
      onClick: handleAcknowledgeClick,
      Icon: CheckOutlineCircle,
    });
  } else {
    if (!review.summaryFilePath) {
      options.push({
        label: 'View Details',
        onClick: handleViewDetailsClick,
        Icon: BulletList,
      });
    }
    if (review.summaryFilePath) {
      options.push({
        label: 'Download file',
        onClick: handleDownloadFile,
        Icon: Download,
      });
    }
  }

  return options;
};
